import React, { useState, useEffect } from 'react';
import axiosInstance from '../../apis/axiosConfig';
import { toast } from 'react-toastify';
import { FaImage, FaTrash, FaPlus, FaCheck, FaTimes, FaToggleOn, FaToggleOff } from 'react-icons/fa';
import { Modal, Button, Form } from 'react-bootstrap';
import '../../pages_premium.css';
import './BannersList.css';
import { getFullImageUrl } from '../../utils/url';
import AdminPagination from '../../components/common/AdminPagination';

function BannersList() {
    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [name, setName] = useState('');
    const [image, setImage] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const fetchBanners = async () => {
        try {
            const res = await axiosInstance.get('/banners/admin/all');
            setBanners(res.data);
        } catch (error) {
            console.error('Error fetching banners:', error);
            toast.error('Failed to fetch banners');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBanners();
    }, []);

    const handleCreateBanner = async (e) => {
        e.preventDefault();
        if (!name || !image) {
            toast.error('Name and Image are required');
            return;
        }

        const formData = new FormData();
        formData.append('name', name);
        formData.append('image', image);

        try {
            await axiosInstance.post('/banners/admin/create', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            toast.success('Banner created successfully');
            setShowModal(false);
            setName('');
            setImage(null);
            fetchBanners();
        } catch (error) {
            console.error('Error creating banner:', error);
            toast.error(error.response?.data?.message || 'Failed to create banner');
        }
    };

    const toggleStatus = async (id) => {
        try {
            await axiosInstance.put(`/banners/admin/${id}/toggle`);
            toast.success(`Banner status updated`);
            fetchBanners();
        } catch (error) {
            console.error('Error toggling status:', error);
            toast.error('Failed to update status');
        }
    };

    const deleteBanner = async (id) => {
        if (!window.confirm('Are you sure you want to delete this banner?')) return;
        try {
            await axiosInstance.delete(`/banners/admin/${id}`);
            toast.success('Banner deleted successfully');
            fetchBanners();
        } catch (error) {
            console.error('Error deleting banner:', error);
            toast.error('Failed to delete banner');
        }
    };

    if (loading) {
        return (
            <div className="banners-list-page page-premium-wrapper">
                <div className="d-flex justify-content-center align-items-center" style={{ height: '70vh' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="sr-only">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="banners-list-page page-premium-wrapper">
            <div className="page-premium-content">
                
                {/* Hero Header */}
                <div className="page-hero-header">
                    <div className="ph-left">
                        <div className="ph-icon">
                            <FaImage />
                        </div>
                        <div>
                            <h1 className="ph-title">Banner Management</h1>
                            <p className="ph-subtitle">Manage homepage hero banners</p>
                        </div>
                    </div>
                    
                    <div className="ph-right">
                        <div className="ph-count-badge">
                            <span className="count-num">{banners.length}</span>
                            <span className="count-lbl">Total Banners</span>
                        </div>
                    </div>
                </div>

                <div className="premium-table-card">
                    <div className="ptable-toolbar">
                        <div className="ptable-title">
                            <span className="ptable-title-dot"></span> 
                            All Banners
                        </div>
                        <button className="add-banner-btn" onClick={() => setShowModal(true)}>
                            <FaPlus /> Upload Banner
                        </button>
                    </div>
                    
                    <div className="ptable-wrap">
                        <table className="premium-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Preview</th>
                                    <th>Name</th>
                                    <th>Status</th>
                                    <th className="text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {banners.slice((currentPage - 1) * pageSize, currentPage * pageSize).map((banner, idx) => (
                                    <tr key={banner.id}>
                                        <td style={{ color: '#64748b', fontWeight: 600 }}>#{(currentPage - 1) * pageSize + idx + 1}</td>
                                        <td>
                                            <img 
                                                src={getFullImageUrl(banner.image)} 
                                                alt={banner.name}
                                                className="banner-preview-img"
                                            />
                                        </td>
                                        <td>
                                            <strong style={{ color: '#0f172a' }}>{banner.name}</strong>
                                        </td>
                                        <td>
                                            <span className={`ptag ${banner.is_active ? 'ptag-active' : 'ptag-banned'}`}>
                                                {banner.is_active ? <FaCheck style={{ marginRight: 4 }} /> : <FaTimes style={{ marginRight: 4 }} />}
                                                {banner.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="text-center">
                                            <div className="action-btn-group">
                                                <button 
                                                    className="icon-action-btn icon-action-btn-toggle"
                                                    onClick={() => toggleStatus(banner.id)}
                                                    title={banner.is_active ? 'Deactivate Banner' : 'Activate Banner'}
                                                >
                                                    {banner.is_active ? <FaToggleOn style={{ fontSize: '18px', color: '#10b981' }} /> : <FaToggleOff style={{ fontSize: '18px', color: '#64748b' }} />}
                                                </button>
                                                <button 
                                                    className="icon-action-btn icon-action-btn-delete"
                                                    onClick={() => deleteBanner(banner.id)}
                                                    title="Delete Banner"
                                                >
                                                    <FaTrash />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {banners.length === 0 && (
                                    <tr>
                                        <td colSpan="5" className="text-center text-muted py-5">
                                            No banners found. Click &quot;Upload Banner&quot; to add one!
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <AdminPagination
                        totalItems={banners.length}
                        currentPage={currentPage}
                        pageSize={pageSize}
                        onPageChange={setCurrentPage}
                        onPageSizeChange={(newSize) => { setPageSize(newSize); setCurrentPage(1); }}
                        itemLabel="banners"
                    />
                </div>
            </div>

            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: '1.1rem', fontWeight: 700 }}>Upload New Banner</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleCreateBanner}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ fontWeight: 600, fontSize: '0.875rem' }}>Banner Name <span className="text-danger">*</span></Form.Label>
                            <Form.Control type="text" required placeholder="e.g. Summer Sale 2026"
                                value={name} 
                                onChange={(e) => setName(e.target.value)} />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label style={{ fontWeight: 600, fontSize: '0.875rem' }}>Banner Image <span className="text-danger">*</span></Form.Label>
                            <Form.Control type="file" required accept="image/*"
                                onChange={(e) => setImage(e.target.files[0])} />
                            <Form.Text className="text-muted" style={{ fontSize: '0.78rem' }}>
                                Recommended size: 1920x600 pixels.
                            </Form.Text>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="light" onClick={() => setShowModal(false)}>Close</Button>
                        <Button style={{ background: 'linear-gradient(90deg, #F1191E 0%, #FA5417 50%, #FF7409 100%)', border: 'none' }} type="submit">Upload</Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
}

export default BannersList;
